import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dimmer, Icon, Loader } from 'semantic-ui-react';
import moment from 'moment';
import { Group } from '../../types/Group';
import { isDefaultGroup } from '../../utils/group';
import { Document } from '../../types/Document';
import { addDocumentToGroup, deleteDocumentFromGroup, fetchByDocIds } from '../../services/Documents';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import sendToast from '../../../utils/Toast';

interface Props {
    facilityId: string;
    residentId: string;
    selectedGroup: Group | null;
}

const GroupDocumentsWidget = ({ facilityId, residentId, selectedGroup }: Props) => {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
    const [pendingDeleteDoc, setPendingDeleteDoc] = useState<Document | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [groupDocIdsByGroupId, setGroupDocIdsByGroupId] = useState<Record<string, string[]>>({});
    const fileInputRef = useRef<HTMLInputElement>(null);

    const selectedGroupId = selectedGroup?._id;

    const activeDocIds = useMemo(() => {
        if (!selectedGroupId || !selectedGroup) return [];
        const overriddenDocIds = groupDocIdsByGroupId[selectedGroupId];
        return (overriddenDocIds || selectedGroup.DocIds || []).map(String);
    }, [groupDocIdsByGroupId, selectedGroup, selectedGroupId]);

    const isFacilityOrDefaultGroup = useMemo(() => isDefaultGroup(selectedGroup), [selectedGroup]);

    const canUpload = !!selectedGroup && !isFacilityOrDefaultGroup;

    useEffect(() => {
        let isCancelled = false;

        const loadDocuments = async () => {
            if (!selectedGroupId) {
                setDocuments([]);
                setErrorMessage(null);
                setIsLoading(false);
                return;
            }

            if (activeDocIds.length === 0) {
                setDocuments([]);
                setErrorMessage(null);
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setErrorMessage(null);
                const { Result } = await fetchByDocIds(facilityId, activeDocIds);
                if (!isCancelled) {
                    setDocuments(Result || []);
                }
            } catch (error) {
                if (!isCancelled) {
                    setDocuments([]);
                    setErrorMessage(error instanceof Error ? error.message : 'Error fetching group documents');
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadDocuments();
        return () => {
            isCancelled = true;
        };
    }, [activeDocIds, facilityId, selectedGroupId]);

    const handleUploadClick = () => {
        if (!canUpload || isUploading) return;
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        const currentGroup = selectedGroup;
        if (!selectedFile || !currentGroup) {
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        const currentGroupId = String(currentGroup._id);

        try {
            setIsUploading(true);
            const { docId } = await addDocumentToGroup(facilityId, residentId, {
                Name: selectedFile.name,
                file: selectedFile,
                groupId: currentGroupId,
            });

            setGroupDocIdsByGroupId(prev => {
                const existingDocIds = prev[currentGroupId] || (currentGroup.DocIds || []).map(String);
                if (existingDocIds.includes(docId)) return prev;
                return {
                    ...prev,
                    [currentGroupId]: [...existingDocIds, docId],
                };
            });
            sendToast('success', 'Document uploaded successfully');
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Error uploading document');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const canDeleteDocument = (doc: Document) => {
        return !!doc.UploadedByResidentId && String(doc.UploadedByResidentId) === String(residentId);
    };

    const requestDeleteDocument = (doc: Document) => {
        if (!canDeleteDocument(doc)) {
            sendToast('error', 'You are not authorized to delete this document');
            return;
        }
        setPendingDeleteDoc(doc);
    };

    const handleDeleteDocument = async () => {
        if (!selectedGroup || !pendingDeleteDoc) return;
        const doc = pendingDeleteDoc;

        const groupId = String(selectedGroup._id);

        try {
            setDeletingDocId(doc._id);
            await deleteDocumentFromGroup(facilityId, doc._id, residentId);

            setDocuments(prev => prev.filter(d => d._id !== doc._id));
            setGroupDocIdsByGroupId(prev => {
                const existingDocIds = prev[groupId] || (selectedGroup.DocIds || []).map(String);
                return {
                    ...prev,
                    [groupId]: existingDocIds.filter(id => String(id) !== String(doc._id)),
                };
            });
            setPendingDeleteDoc(null);
            sendToast('success', 'Document deleted successfully');
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Error deleting document');
        } finally {
            setDeletingDocId(null);
        }
    };

    const renderBody = () => {
        if (!selectedGroup) {
            return <p className="group-documents-placeholder">Select a group to view documents</p>;
        }

        return (
            <>
                <div className="group-documents-selected-group">{selectedGroup.Name}</div>

                {isLoading && (
                    <div className="group-documents-loader">
                        <Dimmer active inverted>
                            <Loader />
                        </Dimmer>
                    </div>
                )}

                {!isLoading && errorMessage && (
                    <p className="group-documents-error">{errorMessage}</p>
                )}

                {!isLoading && !errorMessage && documents.length === 0 && (
                    <p className="group-documents-empty">No documents in this group</p>
                )}

                {!isLoading && !errorMessage && documents.length > 0 && (
                    <div className="group-documents-list">
                        {documents.map(doc => {
                            const attachedLink = doc.attachedFiles?.[0]?.attachedLink;
                            const addedBy = doc.UploadedByResidentName || doc.AddedByName || '-';
                            const dateAdded = doc.DateAdded ? moment(doc.DateAdded).format('MM/DD/YYYY, hh:mm a') : '-';
                            const canDelete = canDeleteDocument(doc);
                            const isDeleting = deletingDocId === doc._id;
                            return (
                                <div key={doc._id} className="group-document-item">
                                    <div className="group-document-top">
                                        {attachedLink ? (
                                            <a href={attachedLink} target="_blank" rel="noopener noreferrer" className="group-document-name">
                                                {doc.Name}
                                            </a>
                                        ) : (
                                            <span className="group-document-name">{doc.Name}</span>
                                        )}
                                        {canDelete && (
                                            <Button
                                                icon="trash"
                                                color="red"
                                                basic
                                                size="mini"
                                                className="group-document-delete-btn"
                                                loading={isDeleting}
                                                disabled={isDeleting}
                                                onClick={() => requestDeleteDocument(doc)}
                                                aria-label="Delete document"
                                            />
                                        )}
                                    </div>
                                    <div className="group-document-meta">
                                        <span>{addedBy}</span>
                                        <span>{dateAdded}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="group-documents-widget">
            <div className="section-header group-documents-header">
                <div className="group-documents-title">
                    <Icon name="file outline" />
                    <span>Documents</span>
                </div>
                {canUpload && (
                    <Button
                        size="mini"
                        className="group-documents-upload-btn"
                        icon="upload"
                        content="Upload"
                        loading={isUploading}
                        disabled={isUploading}
                        onClick={handleUploadClick}
                    />
                )}
            </div>

            <div className="group-documents-body">
                <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept="*/*"
                />
                {renderBody()}
            </div>

            <DeleteConfirmationModal
                open={!!pendingDeleteDoc}
                onClose={() => {
                    if (!deletingDocId) setPendingDeleteDoc(null);
                }}
                onConfirm={handleDeleteDocument}
                messageToDisplay="Are you sure you want to delete this document? This cannot be undone."
                loading={!!deletingDocId}
            />
        </div>
    );
};

export default GroupDocumentsWidget;
